import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Building2 } from 'lucide-react';
import { createCustomer, listCustomers, updateOrgStatus } from '../../api/customers';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card } from '../../components/ui/Card';
import { Table, Thead, Th, Tr, Td } from '../../components/ui/Table';
import { StatusBadge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Alert } from '../../components/ui/Alert';
import type { CustomerOrganisation } from '../../types/api';

export function CustomersListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { data, isLoading } = useQuery({ queryKey: ['customers'], queryFn: listCustomers });
  const [modalOpen, setModalOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState<CustomerOrganisation | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);

  const statusMutation = useMutation({
    mutationFn: () =>
      updateOrgStatus(statusTarget!.id, statusTarget!.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'),
    onSuccess: (org) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customer', org.id] });
      showToast(`${org.name} ${org.status === 'ACTIVE' ? 'reactivated' : 'deactivated'}.`);
      setStatusTarget(null);
    },
    onError: (err) => setStatusError(apiErrorMessage(err, 'Could not update this customer.')),
  });

  return (
    <div>
      <PageHeader
        title="Customers"
        description="Every customer organisation on the platform."
        actions={
          <Button onClick={() => setModalOpen(true)}>
            <Plus className="h-4 w-4" /> New customer
          </Button>
        }
      />

      <Card>
        {isLoading ? (
          <div className="py-14 text-center">
            <Spinner />
          </div>
        ) : !data || data.length === 0 ? (
          <EmptyState
            icon={Building2}
            title="No customers yet"
            description="Create your first customer organisation to get started."
            action={<Button onClick={() => setModalOpen(true)}>New customer</Button>}
          />
        ) : (
          <Table>
            <Thead>
              <tr>
                <Th>Name</Th>
                <Th>Status</Th>
                <Th>Default charge type</Th>
                <Th>Created</Th>
                <Th></Th>
              </tr>
            </Thead>
            <tbody>
              {data.map((org) => (
                <Tr key={org.id} className="cursor-pointer" onClick={() => navigate(`/customers/${org.id}`)}>
                  <Td className="font-medium text-slate-900">{org.name}</Td>
                  <Td>
                    <StatusBadge status={org.status} kind="org" />
                  </Td>
                  <Td>{org.billingConfig?.defaultChargeType ?? '-'}</Td>
                  <Td>{new Date(org.createdAt).toLocaleDateString()}</Td>
                  <Td className="text-right">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setStatusError(null);
                        setStatusTarget(org);
                      }}
                    >
                      {org.status === 'ACTIVE' ? 'Deactivate' : 'Reactivate'}
                    </Button>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <NewCustomerModal open={modalOpen} onClose={() => setModalOpen(false)} />

      <ConfirmModal
        open={!!statusTarget}
        onClose={() => setStatusTarget(null)}
        onConfirm={() => statusMutation.mutate()}
        title={statusTarget?.status === 'ACTIVE' ? 'Deactivate customer' : 'Reactivate customer'}
        description={
          statusTarget?.status === 'ACTIVE'
            ? `${statusTarget?.name} will lose portal access and won't appear in ordering flows. Its history (orders, invoices, assets) is kept and this can be reversed at any time.`
            : `${statusTarget?.name} will regain portal access.`
        }
        confirmLabel={statusTarget?.status === 'ACTIVE' ? 'Deactivate' : 'Reactivate'}
        tone={statusTarget?.status === 'ACTIVE' ? 'danger' : 'primary'}
        isLoading={statusMutation.isPending}
        error={statusError}
      />
    </div>
  );
}

function NewCustomerModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [chargeType, setChargeType] = useState<'ONE_OFF' | 'MANAGED'>('ONE_OFF');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => createCustomer({ name, defaultChargeType: chargeType }),
    onSuccess: (org) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      showToast(`${org.name} created.`);
      setName('');
      onClose();
      navigate(`/customers/${org.id}`);
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not create the customer.')),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New customer organisation"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button form="new-customer-form" type="submit" isLoading={mutation.isPending}>
            Create
          </Button>
        </>
      }
    >
      <form id="new-customer-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && <Alert tone="error">{error}</Alert>}
        <Input label="Name" required value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        <Select
          label="Default charge type"
          value={chargeType}
          onChange={(e) => setChargeType(e.target.value as 'ONE_OFF' | 'MANAGED')}
        >
          <option value="ONE_OFF">One-off</option>
          <option value="MANAGED">Managed</option>
        </Select>
      </form>
    </Modal>
  );
}
